import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthService from '../services/AuthService';
import { emit } from '../socket';

const USE_CASE_KEYS = ["fitness", "health", "cooking", "baby", "work", "fishing", "leisure", "custom"];
const USE_CASE_ICONS = { fitness: "🏋️", health: "❤️", cooking: "🍳", baby: "👶", work: "💼", fishing: "🎣", leisure: "🎮", custom: "⚡" };

function RegisterPage() {
  const { t } = useTranslation();
  const USE_CASES = USE_CASE_KEYS.map((key) => ({
    key,
    icon: USE_CASE_ICONS[key],
    label: t(`categories.${key}`),
    desc: t(`useCases.${key}`),
  }));
  const [mode, setMode] = useState('device');
  const [step, setStep] = useState(1);
  const [deviceId, setDeviceId] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Virtual device state
  const [vdName, setVdName] = useState('');
  const [vdEmail, setVdEmail] = useState('');
  const [vdReason, setVdReason] = useState('');
  const [vdSubmitted, setVdSubmitted] = useState(false);
  const [vdRequestId, setVdRequestId] = useState(null);
  const [vdStatus, setVdStatus] = useState(null);

  document.getElementsByTagName('body')[0].className = 'page-sidebar-hidden';

  const handleCheckDevice = async (e) => {
    e.preventDefault();
    setError(null);
    if (!deviceId.trim()) {
      setError(t("register.errDeviceId"));
      return;
    }
    setLoading(true);
    try {
      const result = await AuthService.checkDevice(deviceId.trim());
      if (!result.exists) {
        setError(t("register.errDeviceNotFound"));
      } else if (result.registered) {
        setError(t("register.errDeviceAlreadyRegistered"));
      } else {
        setStep(2);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    if (pin.length < 4) {
      setError(t("register.errPinMin"));
      return;
    }
    if (pin !== confirmPin) {
      setError(t("register.errPinMismatch"));
      return;
    }
    setLoading(true);
    try {
      await AuthService.register(deviceId.trim(), pin);
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVirtualDeviceRequest = async (e) => {
    e.preventDefault();
    setError(null);
    if (!vdName.trim() || vdName.trim().length < 2) {
      setError(t("register.errNameMin"));
      return;
    }
    setLoading(true);
    try {
      const res = await emit('auth:requestVirtualDevice', {
        requesterName: vdName.trim(),
        email: vdEmail.trim() || undefined,
        reason: vdReason.trim() || undefined,
      });
      setVdRequestId(res.data.id);
      setVdSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!vdRequestId) return;
    setError(null);
    setLoading(true);
    try {
      const res = await emit('auth:checkVirtualDeviceRequest', { requestId: vdRequestId });
      setVdStatus(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="container">
        <div className="row justify-content-md-center">
          <div className="col-md-12 col-lg-6">
            <div className="card login-box-container">
              <div className="card-body">
                <div className="authent-logo">
                  <img src={'./logo-kronix.png'} alt={t("login.logoAlt")} style={{ maxWidth: '100%' }} />
                </div>

                {/* Mode Toggle */}
                <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderRadius: 12, overflow: 'hidden', border: '0.5px solid rgba(255,255,255,0.12)' }}>
                  <button
                    type="button"
                    onClick={() => { setMode('device'); setError(null); }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      background: mode === 'device' ? 'rgba(10,132,255,0.2)' : 'transparent',
                      color: mode === 'device' ? '#0A84FF' : 'rgba(255,255,255,0.4)',
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {t("register.iHaveDevice")}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('virtual'); setError(null); }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      background: mode === 'virtual' ? 'rgba(10,132,255,0.2)' : 'transparent',
                      color: mode === 'virtual' ? '#0A84FF' : 'rgba(255,255,255,0.4)',
                      border: 'none',
                      borderLeft: '0.5px solid rgba(255,255,255,0.12)',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {t("register.virtualDevice")}
                  </button>
                </div>

                {/* Device Mode */}
                {mode === 'device' && (
                  <>
                    <div className="authent-text">
                      <p>{step === 1 ? t("register.registerDevice") : t("register.setPin")}</p>
                    </div>

                    {step === 1 ? (
                      <form onSubmit={handleCheckDevice}>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="text"
                              className="form-control"
                              id="floatingDeviceId"
                              placeholder={t("login.deviceId")}
                              value={deviceId}
                              onChange={(e) => setDeviceId(e.target.value)}
                            />
                            <label htmlFor="floatingDeviceId">{t("login.deviceId")}</label>
                          </div>
                        </div>
                        <div className="d-grid">
                          <button type="submit" className="btn btn-info m-b-xs" disabled={loading}>
                            {loading ? t("register.checking") : t("register.continue")}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <form onSubmit={handleRegister}>
                        <div className="mb-3">
                          <div className="alert alert-success" role="alert" dangerouslySetInnerHTML={{ __html: t("register.deviceFound", { deviceId }) }} />
                        </div>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="password"
                              className="form-control"
                              id="floatingPin"
                              placeholder={t("login.pin")}
                              value={pin}
                              onChange={(e) => setPin(e.target.value)}
                            />
                            <label htmlFor="floatingPin">{t("register.pinMinChars")}</label>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="password"
                              className="form-control"
                              id="floatingConfirmPin"
                              placeholder={t("register.confirmPin")}
                              value={confirmPin}
                              onChange={(e) => setConfirmPin(e.target.value)}
                            />
                            <label htmlFor="floatingConfirmPin">{t("register.confirmPin")}</label>
                          </div>
                        </div>
                        <div className="d-grid">
                          <button type="submit" className="btn btn-info m-b-xs" disabled={loading}>
                            {loading ? t("register.registering") : t("register.register")}
                          </button>
                        </div>
                        <div className="d-grid mt-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => { setStep(1); setError(null); setPin(''); setConfirmPin(''); }}
                          >
                            {t("common.back")}
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                )}

                {/* Virtual Device Mode */}
                {mode === 'virtual' && (
                  <>
                    <div className="authent-text">
                      <p>{t("register.requestVirtualDevice")}</p>
                    </div>

                    {!vdSubmitted ? (
                      <form onSubmit={handleVirtualDeviceRequest}>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="text"
                              className="form-control"
                              id="vdName"
                              placeholder={t("register.yourName")}
                              value={vdName}
                              onChange={(e) => setVdName(e.target.value)}
                            />
                            <label htmlFor="vdName">{t("register.yourName")}</label>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="email"
                              className="form-control"
                              id="vdEmail"
                              placeholder={t("register.emailOptional")}
                              value={vdEmail}
                              onChange={(e) => setVdEmail(e.target.value)}
                            />
                            <label htmlFor="vdEmail">{t("register.emailOptional")}</label>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="text"
                              className="form-control"
                              id="vdReason"
                              placeholder={t("register.reasonOptional")}
                              value={vdReason}
                              onChange={(e) => setVdReason(e.target.value)}
                            />
                            <label htmlFor="vdReason">{t("register.reasonOptional")}</label>
                          </div>
                        </div>
                        <div className="d-grid">
                          <button type="submit" className="btn btn-info m-b-xs" disabled={loading}>
                            {loading ? t("register.submitting") : t("register.submitRequest")}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div>
                        <div className="alert alert-success" role="alert" style={{ marginBottom: 16 }}>
                          {t("register.requestSubmitted")}
                        </div>

                        {vdStatus && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{
                              background: 'rgba(255,255,255,0.06)',
                              border: '0.5px solid rgba(255,255,255,0.12)',
                              borderRadius: 12,
                              padding: 16,
                            }}>
                              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 8 }}>
                                {t("common.status")}
                              </div>
                              <div style={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: vdStatus.status === 'approved' ? '#30D158' : vdStatus.status === 'rejected' ? '#FF453A' : '#FFD60A',
                                marginBottom: vdStatus.assignedDeviceId ? 12 : 0,
                                textTransform: 'uppercase',
                              }}>
                                {vdStatus.status}
                              </div>
                              {vdStatus.assignedDeviceId && (
                                <div>
                                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>{t("register.yourVirtualDeviceId")}</div>
                                  <div style={{ fontSize: 18, fontWeight: 800, color: '#0A84FF', fontFamily: 'monospace' }}>
                                    {vdStatus.assignedDeviceId}
                                  </div>
                                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>
                                    {t("register.useIdToRegister")}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="d-grid">
                          <button
                            type="button"
                            className="btn btn-info m-b-xs"
                            onClick={handleCheckStatus}
                            disabled={loading}
                          >
                            {loading ? t("register.checking") : t("register.checkStatus")}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {error && (
                  <div className="authent-text" style={{ color: '#FF453A', marginTop: '10px' }}>
                    {error}
                  </div>
                )}

                <div className="authent-text" style={{ marginTop: '16px' }}>
                  <p>{t("register.alreadyRegistered")} <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>{t("register.signIn")}</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
